import { useEffect, useMemo, useRef } from 'react';

import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { Box } from '@material-ui/core';

import { SonarInstanceName } from '../../utils/QualityGatesApi';
import {
  VCS_PROVIDER_GITHUB,
  resolveSonarOrganization,
  useQualityGates,
} from '../../utils/qualityGates';
import { PickerSelect } from '../PickerSelect';

export const QualityGatePickerField = (
  props: FieldExtensionComponentProps<string>,
) => {
  const {
    onChange,
    rawErrors,
    formData,
    formContext,
    name,
    schema: { title = 'Quality Gate', description = '' } = {},
    required = false,
    uiSchema,
  } = props;

  // Prefer rjsf field value (stable across remounts) over formContext lookup (H1)
  const selectedQualityGate =
    (typeof formData === 'string' ? formData : '') ||
    (formContext?.formData?.[name] as string) ||
    '';

  const wholeFormData = (formContext?.formData || {}) as Record<
    string,
    unknown
  >;
  const uiOptions = (uiSchema?.['ui:options'] || {}) as Record<string, unknown>;

  const vcsProviderField =
    (uiOptions.vcsProviderField as string) || 'vcs_provider';
  // undefined is treated as default (bitbucket) for Sonar org/instance resolution
  const vcsProvider = wholeFormData[vcsProviderField] as string | undefined;

  const organization = resolveSonarOrganization({
    formData: wholeFormData,
    uiOptions,
    vcsProvider,
  });

  const defaultValue =
    (uiOptions.defaultValue as string) || 'The Foundry Way';
  const staticOptions = uiOptions.options as string[] | undefined;

  // undefined / non-github → default SonarCloud instance (bitbucket token)
  const instance: SonarInstanceName =
    vcsProvider === VCS_PROVIDER_GITHUB ? 'github' : 'default';

  const shouldFetch = !staticOptions;
  const { qualityGates, loading, error, usingFallback } = useQualityGates(
    shouldFetch ? organization : undefined,
    instance,
  );

  const options = useMemo(() => {
    if (staticOptions) {
      return staticOptions;
    }
    return qualityGates.map(gate => gate.name);
  }, [staticOptions, qualityGates]);

  const defaultAppliedRef = useRef(false);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Apply default once options are known (H1: avoid depending on onChange identity)
  useEffect(() => {
    if (defaultAppliedRef.current || selectedQualityGate || !defaultValue) {
      return;
    }
    if (shouldFetch && (loading || !organization)) {
      return;
    }
    const match = options.find(
      o => o.toLowerCase() === defaultValue.toLowerCase(),
    );
    if (match) {
      defaultAppliedRef.current = true;
      onChangeRef.current(match);
    }
  }, [
    selectedQualityGate,
    defaultValue,
    options,
    shouldFetch,
    loading,
    organization,
  ]);

  const pickerOptions = options.map(o => ({ value: o, label: o }));

  const missingOrgMessage =
    vcsProvider === VCS_PROVIDER_GITHUB
      ? 'Select a GitHub organization to load Quality Gates'
      : 'Select an organization to load Quality Gates';

  let fetchErrorText: string | undefined;
  if (error && usingFallback) {
    fetchErrorText =
      'SonarCloud unavailable — showing fallback Quality Gates. You can still submit.';
  } else if (error && options.length === 0) {
    fetchErrorText =
      'Unable to load Quality Gates from SonarCloud. Please try again.';
  } else if (!staticOptions && !organization) {
    fetchErrorText = missingOrgMessage;
  }

  const errorText =
    (rawErrors?.[0] as string | undefined) || fetchErrorText || undefined;

  return (
    <Box>
      <PickerSelect
        title={title}
        description={description}
        required={required}
        value={selectedQualityGate}
        onChange={onChange}
        options={pickerOptions}
        loading={shouldFetch && !!organization && loading}
        loadingMessage="Loading Quality Gates..."
        labelId="quality-gate-picker-label"
        errorText={errorText}
      />
    </Box>
  );
};
